import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  Skeleton,
  alpha,
  keyframes,
} from "@mui/material";
import {
  EmailOutlined,
  Visibility,
  VisibilityOff,
  LockOutlined,
  LightMode,
  DarkMode,
  ArrowForwardOutlined,
} from "@mui/icons-material";
import { TranslateContext } from "../../context/TranslateContext";
import { useThemeData } from "../../context/ThemeContext";
import { GlobalContext } from "../../context/GlobalContext";
import { useHistory, useLocation, Link } from "react-router-dom";
import UserLoginGoogle from "./UserLoginGoogle";
import AuthTurnstile from "./AuthTurnstile";
import ForgotPassword from "./ForgotPassword";
import AuthVisualPanel from "./AuthVisualPanel";
import { mediaSrc } from "../../utils/mediaSrc";
import { completeUserAuth } from "../../utils/postLoginRedirect";
import { captureReferralFromUrl } from "../../utils/referralAttribution";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const shimmerBtn = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const UserLogin = () => {
  const { lang } = React.useContext(TranslateContext);
  const [web, setWeb] = React.useState(null);
  const history = useHistory();
  const location = useLocation();
  const authQuery = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const checkoutReturn = authQuery.get("returnTo");
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { mode, toggleColorMode } = useThemeData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  React.useEffect(() => {
    if (authQuery.get("mode") === "register") {
      const next = new URLSearchParams(authQuery);
      next.delete("mode");
      const qs = next.toString();
      history.replace(qs ? `/user/signup?${qs}` : "/user/signup");
    }
  }, [authQuery, history]);

  React.useEffect(() => {
    captureReferralFromUrl();
  }, []);

  const handleGetWeb = useCallback(async () => {
    const res = await hitAxios({
      path: "/api/web/get_web_public",
      post: false,
      admin: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) setWeb(res.data.data);
  }, [hitAxios]);

  React.useEffect(() => {
    handleGetWeb();
  }, [handleGetWeb]);

  React.useEffect(() => {
    (async () => {
      const res = await hitAxios({
        path: "/api/user/register_meta",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        setTurnstileSiteKey(res.data?.data?.turnstile_site_key || null);
      }
    })();
  }, [hitAxios]);

  const siteName = web?.site_name || lang?.authDefaultSite || "Beyund AI";
  const logoUrl = web?.site_logo ? mediaSrc(web.site_logo) : null;
  const metaTitle = web?.meta_title || siteName;

  React.useEffect(() => {
    if (metaTitle) document.title = metaTitle;
  }, [metaTitle]);

  const signupHref = checkoutReturn
    ? `/user/signup?returnTo=${encodeURIComponent(checkoutReturn)}`
    : "/user/signup";

  async function handleSubmit() {
    const email = form.email.trim();
    const password = form.password;

    if (!email || !password) {
      showSnack(lang?.authFieldsRequired || "Email and password are required", false);
      return;
    }
    if (turnstileSiteKey && !turnstileToken) {
      showSnack(lang?.authCompleteSecurityCheck || "Please complete the security check.", false);
      return;
    }

    setSubmitting(true);
    try {
      const res = await hitAxios({
        path: "/api/user/login",
        post: true,
        admin: false,
        obj: { email, password, turnstile_token: turnstileToken || undefined },
        showLoading: false,
      });

      if (res?.data?.success && res.data.token) {
        showSnack(lang?.successMessage || "Success! Logging you in...", true);
        await completeUserAuth({
          token: res.data.token,
          hitAxios,
          history,
          isNewAccount: false,
          returnTo: checkoutReturn,
        });
      } else if (res?.data?.msg) {
        showSnack(res.data.msg, false);
        setTurnstileToken("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      transition: "box-shadow 0.25s ease, border-color 0.25s ease",
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        bgcolor: "background.default",
      }}
    >
      <AuthVisualPanel
        web={web}
        lang={lang}
        siteName={siteName}
        logoUrl={logoUrl}
        isMobile={isMobile}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 4, md: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-15%",
            width: 360,
            height: 360,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <IconButton
          onClick={toggleColorMode}
          sx={{
            position: "absolute",
            top: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
            "&:hover": { transform: "scale(1.06)" },
            transition: "transform 0.2s ease",
          }}
        >
          {mode === "dark" ? (
            <LightMode sx={{ color: "warning.main" }} />
          ) : (
            <DarkMode sx={{ color: "primary.main" }} />
          )}
        </IconButton>

        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            animation: `${slideIn} 0.55s ease-out`,
          }}
        >
          <Box mb={3}>
            {web === null ? (
              <>
                <Skeleton variant="text" width="75%" height={44} />
                <Skeleton variant="text" width="55%" height={24} sx={{ mt: 1 }} />
              </>
            ) : (
              <>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  letterSpacing="-0.03em"
                  fontSize={{ xs: "1.65rem", md: "2rem" }}
                  gutterBottom
                >
                  {`${lang?.welcomeBack || "Welcome back"} 👋`}
                </Typography>
                <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                  {lang?.signInSubtitle || "Sign in to continue your AI-powered journey"}
                </Typography>
              </>
            )}
          </Box>

          <UserLoginGoogle hitAxios={hitAxios} web={web} returnTo={checkoutReturn} />

          <Stack spacing={2}>
            <TextField
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              placeholder={lang?.emailPlaceholder || lang?.email || "Email"}
              type="email"
              autoComplete="email"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
              placeholder={lang?.passwordPlaceholder || lang?.password || "Password"}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {turnstileSiteKey ? (
              <AuthTurnstile
                siteKey={turnstileSiteKey}
                onToken={(token) => setTurnstileToken(token || "")}
                onExpire={() => setTurnstileToken("")}
                onError={() => {}}
              />
            ) : null}

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              fullWidth
              variant="contained"
              size="large"
              endIcon={!submitting && <ArrowForwardOutlined />}
              sx={{
                py: 1.6,
                fontWeight: 700,
                borderRadius: 2.5,
                textTransform: "none",
                fontSize: "1rem",
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light}, ${theme.palette.primary.main})`,
                backgroundSize: "200% auto",
                animation: submitting ? "none" : `${shimmerBtn} 4s linear infinite`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
                "&:hover": {
                  boxShadow: `0 12px 36px ${alpha(theme.palette.primary.main, 0.45)}`,
                  transform: "translateY(-1px)",
                },
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              {submitting
                ? lang?.authPleaseWait || "Please wait…"
                : lang?.continueWithEmail || "Continue with email"}
            </Button>
          </Stack>

          <ForgotPassword lang={lang} />

          <Typography variant="body2" color="text.secondary" textAlign="center" mt={3}>
            {lang?.authNoAccount || "Don't have an account?"}{" "}
            <Box
              component={Link}
              to={signupHref}
              sx={{
                color: "primary.main",
                fontWeight: 700,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {lang?.authSignUp || "Sign up"}
            </Box>
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            display="block"
            mt={3}
            fontSize="0.75rem"
            lineHeight={1.6}
          >
            {lang?.byContinuing || "By continuing, you agree to our"}{" "}
            <Box
              component="span"
              onClick={() => window.open("/terms-and-conditions")}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {lang?.termsOfService || "Terms of Service"}
            </Box>{" "}
            {lang?.and || "and"}{" "}
            <Box
              component="span"
              onClick={() => window.open("/privacy-policy")}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {lang?.privacyPolicy || "Privacy Policy"}
            </Box>
          </Typography>

          {web?.site_name && (
            <Typography
              variant="caption"
              color="text.disabled"
              textAlign="center"
              display="block"
              mt={2}
              fontSize="0.7rem"
            >
              © {new Date().getFullYear()} {web.site_name}.{" "}
              {lang?.copyrightText || "All rights reserved."}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserLogin;
